import { useState } from 'react';

// Hooks
import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { classNames } from '@/utils';

// Constants/Libs/Types
import { SOCIAL_INBOX_META } from '@/components/pages/conversations/SocialInbox/const';

// Components
import { EmptyState } from '@/components/base/EmptyState';
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import { MessageSkeleton, SocialInboxMessage } from '@/components/pages/conversations/SocialInbox';
import MentionMediaViewerModal from '@/components/pages/listening/widget/MentionMediaViewerModal';

export const ConversationMessageList = ({ className }: { className?: string }) => {
    const { t } = useTranslation('conversations');
    const { currentSocialInbox } = useSocialInboxNavigation();
    const { filteredMessages, isLoading, isLoadingMore, isLoadMore, loadMore } =
        useSocialInboxDataSource();

    const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);

    const handlePostClick = (mediaId: string) => {
        if (currentSocialInbox === SOCIAL_INBOX_META.mention.key) {
            setSelectedMediaId(mediaId);
        }
    };

    const renderMediaModal = () => {
        if (currentSocialInbox === SOCIAL_INBOX_META.mention.key) {
            return (
                <MentionMediaViewerModal
                    isOpen={Boolean(selectedMediaId)}
                    toggle={() => setSelectedMediaId(null)}
                    mediaId={selectedMediaId}
                    mutateMedias={() => {}}
                />
            );
        }
    };

    const renderMessages = () => {
        if (isLoading) {
            return [...Array(5).keys()].map((index) => <MessageSkeleton key={index} />);
        }

        if (!filteredMessages.length) {
            const tabLabel = t(`tabs.${currentSocialInbox}`).toLowerCase();
            return (
                <EmptyState
                    icon={SOCIAL_INBOX_META[currentSocialInbox].icon}
                    size="sm"
                    title={t('empty_state.no_content.title', {
                        item: tabLabel
                    })}
                    description={t('empty_state.no_content.description')}
                />
            );
        }

        return (
            <>
                {filteredMessages.map((message) => (
                    <SocialInboxMessage
                        message={message}
                        key={message.id}
                        onPostClick={handlePostClick}
                    />
                ))}

                {isLoadMore ? (
                    <LoadMoreInfinite loadMore={loadMore} isLoadingMore={isLoadingMore} />
                ) : (
                    /* Spacer div to ensure bottom padding in scroll container */
                    <div className="h-2 w-full" />
                )}
            </>
        );
    };

    return (
        <div
            className={classNames(
                'grow space-y-2 overflow-y-scroll p-px pl-1 pr-4 pt-1',
                className
            )}>
            {renderMediaModal()}

            {renderMessages()}
        </div>
    );
};
